/**
 * Central source of truth for status labels, badge styles, icons, and filter definitions.
 * ALL status-related display text and colors must come from here.
 */
import * as React from "react";
import {
    CheckmarkFilled,
    DismissFilled,
    WarningFilled,
    ArrowSyncFilled,
    SubtractCircleFilled,
    QuestionCircleFilled,
} from "@fluentui/react-icons";
import { DisplayTestStatus } from "../types/TestCasesTab.types";
import { primer } from "../styles/primerDarkTheme";

// ── Display labels ─────────────────────────────────────────────────────────────
// Maps raw DisplayTestStatus → user-facing label shown in badges, filters, stats
export const STATUS_LABEL: Record<DisplayTestStatus, string> = {
    [DisplayTestStatus.Passed]:    "Passed",
    [DisplayTestStatus.Flaky]:     "Flaky",
    [DisplayTestStatus.Failed]:    "Failed",
    [DisplayTestStatus.Skipped]:   "Skipped",
    [DisplayTestStatus.NoResults]: "Skipped",
    [DisplayTestStatus.Unknown]:   "Unknown",
};

// ── Badge styles ───────────────────────────────────────────────────────────────
export const STATUS_BADGE: Record<DisplayTestStatus, { bg: string; text: string }> = {
    [DisplayTestStatus.Passed]:    { bg: primer.greenBg,       text: primer.greenText },
    [DisplayTestStatus.Flaky]:     { bg: primer.flakyYellowBg, text: primer.flakyYellow },
    [DisplayTestStatus.Failed]:    { bg: primer.orangeBg,      text: primer.orangeText },
    [DisplayTestStatus.Skipped]:   { bg: primer.redBg,         text: primer.redText },
    [DisplayTestStatus.NoResults]: { bg: primer.redBg,         text: primer.redText },
    [DisplayTestStatus.Unknown]:   { bg: primer.overlay,       text: primer.fgMuted },
};

// ── Status icons ───────────────────────────────────────────────────────────────
export const STATUS_ICON: Record<DisplayTestStatus, { icon: React.ElementType; color: string }> = {
    [DisplayTestStatus.Passed]:    { icon: CheckmarkFilled,      color: primer.green },
    [DisplayTestStatus.Flaky]:     { icon: ArrowSyncFilled,      color: primer.flakyYellow },
    [DisplayTestStatus.Failed]:    { icon: DismissFilled,        color: primer.orange },
    [DisplayTestStatus.Skipped]:   { icon: WarningFilled,        color: primer.red },
    [DisplayTestStatus.NoResults]: { icon: SubtractCircleFilled, color: primer.fgMuted },
    [DisplayTestStatus.Unknown]:   { icon: QuestionCircleFilled, color: primer.fgMuted },
};

// ── Filter definitions ─────────────────────────────────────────────────────────
export type FilterKey = "all" | "passed" | "failed" | "flaky" | "skipped";

export interface FilterDef {
    key: FilterKey;
    label: string;
    statuses: DisplayTestStatus[];
}

export const FILTERS: FilterDef[] = [
    { key: "all",     label: "All",     statuses: [] },
    { key: "passed",  label: "Passed",  statuses: [DisplayTestStatus.Passed] },
    { key: "failed",  label: "Failed",  statuses: [DisplayTestStatus.Failed] },
    { key: "flaky",   label: "Flaky",   statuses: [DisplayTestStatus.Flaky] },
    { key: "skipped", label: "Skipped", statuses: [DisplayTestStatus.Skipped, DisplayTestStatus.NoResults] },
];

// Helper: is a given DisplayTestStatus covered by a FilterKey?
export function statusMatchesFilter(status: DisplayTestStatus, key: FilterKey): boolean {
    const def = FILTERS.find(f => f.key === key);
    return def ? def.statuses.includes(status) : false;
}
